// UpdateLead.jsx

import React, { useEffect, useState } from "react";
import axios from "axios";
import ReactSelect from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import { useUserAuth } from "../../context/UserAuthContext";
import { STAGE_OPTIONS } from "./stageOptions";
import { STATUS_OPTIONS } from "./statusOptions";
import {
  FiArrowLeft,
  FiSave,
  FiPlus,
  FiTrash2,
  FiMapPin,
  FiUser,
  FiTag,
  FiLayers,
  FiCalendar,
  FiMessageSquare,
  FiThermometer,
  FiCheckCircle,
  FiBriefcase,
  FiLoader,
} from "react-icons/fi";

const F = "'DM Sans', 'Segoe UI', sans-serif";
const T = {
  bg: "#f0f4f9",
  surface: "#ffffff",
  border: "#e2eaf4",
  text: "#0d1b2e",
  muted: "#64748b",
  accent: "#2563EB",
  accentBg: "#EBF4FF",
  accentHov: "#1d4ed8",
  success: "#059669",
  danger: "#dc2626",
  inputBg: "#f8fafc",
  label: "#374151",
};

/* ─────────────────── SHARED STYLES ─────────────────── */
const card = {
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: 18,
  overflow: "hidden",
  boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
};

const sectionCard = {
  ...card,
  marginBottom: 20,
};

const inputBase = {
  width: "100%",
  height: 44,
  padding: "0 14px",
  border: `1px solid ${T.border}`,
  borderRadius: 10,
  fontSize: 14,
  fontFamily: F,
  color: T.text,
  background: T.inputBg,
  boxSizing: "border-box",
  outline: "none",
  transition: "border-color .15s, box-shadow .15s",
};

const selectBase = {
  ...inputBase,
  appearance: "none",
  WebkitAppearance: "none",
  cursor: "pointer",
  paddingRight: 36,
};

const labelStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 12,
  fontWeight: 600,
  color: T.label,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: 6,
};

const iconWrap = (color) => ({
  width: 32,
  height: 32,
  borderRadius: 8,
  background: color + "18",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color,
  fontSize: 15,
  flexShrink: 0,
});

/* ─────────────────── LABEL COLORS ─────────────────── */
const LABEL_CFG = {
  Hot: { color: "#ef4444", bg: "#fef2f2", border: "#fca5a5" },
  Warm: { color: "#f59e0b", bg: "#fffbeb", border: "#fcd34d" },
  Cold: { color: "#3b82f6", bg: "#eff6ff", border: "#93c5fd" },
  Dead: { color: "#6b7280", bg: "#f9fafb", border: "#d1d5db" },
};

/* ─────────────────── FORM FIELD WRAPPER ─────────────────── */
const Field = ({ icon, iconColor = T.accent, label, children }) => (
  <div style={{ marginBottom: 20 }}>
    <label style={labelStyle}>
      <span style={iconWrap(iconColor)}>{icon}</span>
      {label}
    </label>
    {children}
  </div>
);

/* ─────────────────── SECTION HEADER ─────────────────── */
const SectionHeader = ({ icon, title, subtitle }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "18px 24px 16px",
      borderBottom: `1px solid ${T.border}`,
    }}
  >
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: 10,
        background: T.accentBg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: T.accent,
        fontSize: 18,
      }}
    >
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: 12, color: T.muted, marginTop: 1 }}>
          {subtitle}
        </div>
      )}
    </div>
  </div>
);

/* ─────────────────── REACT-SELECT STYLES ─────────────────── */
const rsStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    border: `1px solid ${state.isFocused ? T.accent : T.border}`,
    borderRadius: 10,
    background: T.inputBg,
    boxShadow: state.isFocused ? `0 0 0 3px ${T.accent}22` : "none",
    fontFamily: F,
    fontSize: 14,
    cursor: "pointer",
    "&:hover": { borderColor: T.accent },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    border: `1px solid ${T.border}`,
    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
    overflow: "hidden",
  }),
  option: (base, state) => ({
    ...base,
    background: state.isSelected
      ? T.accent
      : state.isFocused
        ? T.accentBg
        : "#fff",
    color: state.isSelected ? "#fff" : T.text,
    fontSize: 13,
    fontFamily: F,
    cursor: "pointer",
    padding: "10px 14px",
    zIndex: 2,
  }),
  placeholder: (base) => ({ ...base, color: T.muted, fontSize: 13 }),
  singleValue: (base) => ({ ...base, color: T.text, fontSize: 14 }),
  indicatorSeparator: () => ({ display: "none" }),
};

/* ═════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const UpdateLead = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useUserAuth();

  const base_url = import.meta.env.VITE_PUBLIC_BASE_URL_DEVELOPMENT;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [venueLoading, setVenueLoading] = useState(false);
  const [lead, setLead] = useState(null);
  const [venues, setVenues] = useState([]);
  const [saved, setSaved] = useState(false);

  const [venueOptions, setVenueOptions] = useState([
    { option_id: "", venue_id: "", vendor: "" },
  ]);

  const [formData, setFormData] = useState({
    lead_status: "",
    stage: "",
    followup_date_time: "",
    lead_label: "",
    remark: "",
  });

  /* ── focus state for inputs ── */
  const [focused, setFocused] = useState("");

  const focusStyle = (name) => ({
    ...inputBase,
    borderColor: focused === name ? T.accent : T.border,
    boxShadow: focused === name ? `0 0 0 3px ${T.accent}22` : "none",
  });

  /* ── fetches ── */
  const fetchVenues = async () => {
    try {
      setVenueLoading(true);
      const res = await axios.get(`${base_url}/api/v1/venues`);
      setVenues(
        res?.data?.data?.map((v) => ({
          value: v.id,
          label: `${v.business_name} (${v.city_name})`,
        })) || [],
      );
    } catch (e) {
      console.log(e);
    } finally {
      setVenueLoading(false);
    }
  };

  const getLeadDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${base_url}/api/v1/leads/single/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res?.data?.data;
      setLead(data);
      setFormData({
        lead_status: data?.lead_status || "",
        stage: data?.stage_key || "",
        followup_date_time: data?.followup_date_time
          ? new Date(data.followup_date_time).toISOString().slice(0, 16)
          : "",
        lead_label: data?.lead_label || "",
        remark: data?.remark || "",
      });
      if (data?.venue_options?.length > 0) {
        setVenueOptions(
          data.venue_options.map((v) => ({
            option_id: v.id,
            venue_id: v.venue_id || "",
            vendor: v.vendor || "",
          })),
        );
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVenues();
    getLeadDetails();
  }, [id]);

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleVenueChange = (index, field, value) => {
    const updated = [...venueOptions];
    updated[index][field] = value;
    setVenueOptions(updated);
  };

  const addRow = () =>
    setVenueOptions((prev) => [
      ...prev,
      { option_id: "", venue_id: "", vendor: "" },
    ]);
  const removeRow = (i) =>
    setVenueOptions((prev) => prev.filter((_, idx) => idx !== i));

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());

    return now.toISOString().slice(0, 16);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Stage Key:", formData);
    if (
      formData?.stage !== "converted" &&
      formData?.stage !== "not_converted"
    ) {
      if (!formData.followup_date_time) {
        alert("Follow-up date & time is required");
        return;
      }

      if (new Date(formData.followup_date_time) <= new Date()) {
        alert("Please select a future follow-up date & time");
        return;
      }
    }

    try {
      setSaving(true);
      await axios.put(
        `${base_url}/api/v1/leads/update/${id}`,
        {
          ...formData,
          merchant_id: lead?.merchant_id,
          venue_options: venueOptions,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        navigate(-1);
      }, 1200);
    } catch (e) {
      console.log(e);
      alert("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  /* ── loader ── */
  if (loading)
    return (
      <div className="page-wrapper">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            gap: 12,
          }}
        >
          <FiLoader
            size={24}
            color={T.accent}
            style={{ animation: "spin .7s linear infinite" }}
          />
          <span style={{ color: T.muted, fontFamily: F, fontSize: 15 }}>
            Loading lead details…
          </span>
        </div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );

  const labelCfg = LABEL_CFG[formData.lead_label] || {};

  return (
    <div className="page-wrapper">
      <div className="container-fluid">
        <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity:0;transform:translateY(8px); } to { opacity:1;transform:none; } }
        .ul-row { animation: fadeIn .22s ease; }
        .ul-btn-add:hover  { background: ${T.success}18 !important; border-color: ${T.success} !important; color: ${T.success} !important; }
        .ul-btn-del:hover  { background: #fef2f2 !important; border-color: ${T.danger} !important; color: ${T.danger} !important; }
        .ul-btn-save:hover { background: ${T.accentHov} !important; }
        .ul-btn-back:hover { background: ${T.inputBg} !important; }
        textarea:focus, input:focus, select:focus { outline: none !important; }
        .venue-row:hover { background: #fafcff !important; }
      `}</style>

        <div
          style={{
            background: T.bg,
            minHeight: "100vh",
            fontFamily: F,
            padding: "24px 20px 60px",
          }}
        >
          <div style={{ margin: "0 auto" }}>
            {/* ── HEADER CARD ── */}
            <div
              style={{
                background: "linear-gradient(135deg, #0f4c8a 0%, #051e3e 100%)",
                borderRadius: 20,
                padding: "24px 28px",
                marginBottom: 20,
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* glow blobs */}
              <div
                style={{
                  position: "absolute",
                  top: -70,
                  right: -70,
                  width: 220,
                  height: 220,
                  borderRadius: "50%",
                  background: "rgba(99,179,237,0.1)",
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: -40,
                  left: 40,
                  width: 140,
                  height: 140,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.04)",
                  pointerEvents: "none",
                }}
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: 14,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: "#fff",
                      letterSpacing: "-0.3px",
                      marginBottom: 8,
                    }}
                  >
                    {lead?.lead_name || lead?.business_name || "Update Lead"}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                    {lead?.contact_person_name && (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: 12,
                          fontWeight: 500,
                          background: "rgba(255,255,255,0.12)",
                          color: "#dce9f5",
                          border: "0.5px solid rgba(255,255,255,0.2)",
                          borderRadius: 20,
                          padding: "3px 11px",
                        }}
                      >
                        <FiUser size={11} />
                        {lead.contact_person_name}
                      </span>
                    )}
                    {lead?.category && (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: 12,
                          fontWeight: 500,
                          background: "rgba(255,255,255,0.12)",
                          color: "#dce9f5",
                          border: "0.5px solid rgba(255,255,255,0.2)",
                          borderRadius: 20,
                          padding: "3px 11px",
                        }}
                      >
                        <FiBriefcase size={11} />
                        {lead.category}
                      </span>
                    )}
                    {lead?.city && (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: 12,
                          fontWeight: 500,
                          background: "rgba(255,255,255,0.12)",
                          color: "#dce9f5",
                          border: "0.5px solid rgba(255,255,255,0.2)",
                          borderRadius: 20,
                          padding: "3px 11px",
                        }}
                      >
                        <FiMapPin size={11} />
                        {lead.city}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className="ul-btn-back"
                  onClick={() => navigate(-1)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "9px 18px",
                    borderRadius: 10,
                    border: "0.5px solid rgba(255,255,255,0.3)",
                    background: "rgba(255,255,255,0.1)",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                    fontFamily: F,
                    transition: "background .15s",
                  }}
                >
                  <FiArrowLeft size={15} /> Back
                </button>
              </div>
            </div>

            {/* ── FORM ── */}
            <form onSubmit={handleSubmit}>
              {/* SECTION 1 — Lead Info */}
              <div style={sectionCard}>
                <SectionHeader
                  icon={<FiTag />}
                  title="Lead Information"
                  subtitle="Update status, stage and label"
                />
                <div style={{ padding: "22px 24px" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
                      gap: "0 24px",
                    }}
                  >
                    {/* LEAD STATUS */}
                    <Field
                      icon={<FiCheckCircle size={15} />}
                      iconColor="#10b981"
                      label="Lead Status"
                    >
                      <div style={{ position: "relative" }}>
                        <select
                          name="lead_status"
                          value={formData.lead_status}
                          onChange={handleChange}
                          onFocus={() => setFocused("lead_status")}
                          onBlur={() => setFocused("")}
                          style={{
                            ...selectBase,
                            borderColor:
                              focused === "lead_status" ? T.accent : T.border,
                            boxShadow:
                              focused === "lead_status"
                                ? `0 0 0 3px ${T.accent}22`
                                : "none",
                          }}
                        >
                          <option value="">Select status</option>
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                        <FiLayers
                          size={13}
                          style={{
                            position: "absolute",
                            right: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: T.muted,
                            pointerEvents: "none",
                          }}
                        />
                      </div>
                    </Field>

                    {/* STAGE */}
                    <Field
                      icon={<FiLayers size={15} />}
                      iconColor="#8b5cf6"
                      label="Pipeline Stage"
                    >
                      <div style={{ position: "relative" }}>
                        <select
                          name="stage"
                          value={formData.stage}
                          onChange={handleChange}
                          onFocus={() => setFocused("stage")}
                          onBlur={() => setFocused("")}
                          style={{
                            ...selectBase,
                            borderColor:
                              focused === "stage" ? T.accent : T.border,
                            boxShadow:
                              focused === "stage"
                                ? `0 0 0 3px ${T.accent}22`
                                : "none",
                          }}
                        >
                          <option value="">Select stage</option>
                          {STAGE_OPTIONS.map((s) => (
                            <option
                              key={s.value}
                              value={s.value}
                              disabled={s.value === "lead_received"}
                            >
                              {s.label} ({s.progress}%)
                            </option>
                          ))}
                        </select>
                        <FiLayers
                          size={13}
                          style={{
                            position: "absolute",
                            right: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: T.muted,
                            pointerEvents: "none",
                          }}
                        />
                      </div>
                    </Field>

                    {/* LEAD LABEL */}
                    <Field
                      icon={<FiThermometer size={15} />}
                      iconColor="#ec4899"
                      label="Lead Label"
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(4,1fr)",
                          gap: 8,
                        }}
                      >
                        {["Hot", "Warm", "Cold", "Duplicate"].map((lbl) => {
                          const cfg = LABEL_CFG[lbl] || {};
                          const active = formData.lead_label === lbl;
                          return (
                            <button
                              key={lbl}
                              type="button"
                              onClick={() =>
                                setFormData((p) => ({ ...p, lead_label: lbl }))
                              }
                              style={{
                                padding: "8px 4px",
                                borderRadius: 10,
                                border: active
                                  ? `2px solid ${cfg.color}`
                                  : `1px solid ${T.border}`,
                                background: active ? cfg.bg : T.surface,
                                color: active ? cfg.color : T.muted,
                                fontWeight: active ? 700 : 500,
                                fontSize: 13,
                                cursor: "pointer",
                                transition: "all .15s",
                                fontFamily: F,
                              }}
                            >
                              {lbl}
                            </button>
                          );
                        })}
                      </div>
                      {/* active label chip */}
                      {formData.lead_label && (
                        <div
                          style={{
                            marginTop: 8,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 12,
                            fontWeight: 600,
                            background: labelCfg.bg,
                            color: labelCfg.color,
                            border: `1px solid ${labelCfg.border}`,
                            borderRadius: 20,
                            padding: "3px 12px",
                          }}
                        >
                          <FiThermometer size={11} /> {formData.lead_label} Lead
                        </div>
                      )}
                    </Field>

                    {/* FOLLOW UP */}
                    <Field
                      icon={<FiCalendar size={15} />}
                      iconColor="#f59e0b"
                      label="Follow Up Date & Time"
                    >
                      <input
                        type="datetime-local"
                        name="followup_date_time"
                        value={formData.followup_date_time}
                        onChange={handleChange}
                        onFocus={() => setFocused("followup")}
                        onBlur={() => setFocused("")}
                        min={getMinDateTime()}
                        style={focusStyle("followup")}
                      />
                    </Field>
                  </div>
                </div>
              </div>

              {/* SECTION 2 — Venue / Vendor */}
              <div style={sectionCard}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "18px 24px 16px",
                    borderBottom: `1px solid ${T.border}`,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        background: T.accentBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: T.accent,
                        fontSize: 18,
                      }}
                    >
                      <FiMapPin />
                    </div>
                    <div>
                      <div
                        style={{ fontSize: 15, fontWeight: 700, color: T.text }}
                      >
                        Venue / Vendor Options
                      </div>
                      <div
                        style={{ fontSize: 12, color: T.muted, marginTop: 1 }}
                      >
                        Shortlisted venues for this lead
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="ul-btn-add"
                    onClick={addRow}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "8px 16px",
                      borderRadius: 10,
                      border: `1px solid ${T.border}`,
                      background: T.surface,
                      color: T.muted,
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: "pointer",
                      fontFamily: F,
                      transition: "all .15s",
                    }}
                  >
                    <FiPlus size={14} /> Add Venue
                  </button>
                </div>

                <div style={{ padding: "16px 24px 20px" }}>
                  {/* column headers */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 80px",
                      gap: 12,
                      marginBottom: 8,
                    }}
                  >
                    {["Venue", "Vendor Name", ""].map((h) => (
                      <div
                        key={h}
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: T.muted,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {h}
                      </div>
                    ))}
                  </div>

                  {venueOptions.map((item, index) => (
                    <div
                      key={index}
                      className="ul-row venue-row"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 80px",
                        gap: 12,
                        alignItems: "center",
                        padding: "10px 12px",
                        borderRadius: 12,
                        background: T.surface,
                        border: `1px solid ${T.border}`,
                        marginBottom: 10,
                        transition: "background .12s",
                      }}
                    >
                      {/* venue select */}
                      <ReactSelect
                        options={venues}
                        styles={rsStyles}
                        value={
                          venues.find(
                            (v) => String(v.value) === String(item.venue_id),
                          ) || null
                        }
                        onChange={(sel) =>
                          handleVenueChange(index, "venue_id", sel?.value || "")
                        }
                        placeholder={venueLoading ? "Loading…" : "Select venue"}
                        isSearchable
                        isLoading={venueLoading}
                      />

                      {/* vendor input */}
                      <input
                        type="text"
                        placeholder="Enter vendor name"
                        value={item.vendor}
                        onChange={(e) =>
                          handleVenueChange(index, "vendor", e.target.value)
                        }
                        onFocus={() => setFocused(`vendor_${index}`)}
                        onBlur={() => setFocused("")}
                        style={focusStyle(`vendor_${index}`)}
                      />

                      {/* remove */}
                      {venueOptions.length > 1 ? (
                        <button
                          type="button"
                          className="ul-btn-del"
                          onClick={() => removeRow(index)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 40,
                            height: 44,
                            borderRadius: 10,
                            border: `1px solid ${T.border}`,
                            background: T.surface,
                            color: T.muted,
                            cursor: "pointer",
                            transition: "all .15s",
                          }}
                        >
                          <FiTrash2 size={15} />
                        </button>
                      ) : (
                        <div />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* SECTION 3 — Remark */}
              <div style={sectionCard}>
                <SectionHeader
                  icon={<FiMessageSquare />}
                  title="Remark"
                  subtitle="Add notes or comments about this lead"
                />
                <div style={{ padding: "20px 24px" }}>
                  <textarea
                    name="remark"
                    rows={5}
                    placeholder="Write your remarks, notes, or follow-up details here…"
                    value={formData.remark}
                    onChange={handleChange}
                    onFocus={() => setFocused("remark")}
                    onBlur={() => setFocused("")}
                    style={{
                      ...inputBase,
                      height: "auto",
                      padding: "12px 14px",
                      resize: "vertical",
                      lineHeight: 1.65,
                      borderColor: focused === "remark" ? T.accent : T.border,
                      boxShadow:
                        focused === "remark"
                          ? `0 0 0 3px ${T.accent}22`
                          : "none",
                    }}
                  />
                </div>
              </div>

              {/* SAVE BAR */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 12,
                  padding: "16px 24px",
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  borderRadius: 16,
                  boxShadow: "0 -2px 16px rgba(0,0,0,0.04)",
                }}
              >
                <button
                  type="button"
                  className="ul-btn-back"
                  onClick={() => navigate(-1)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "11px 22px",
                    borderRadius: 10,
                    border: `1px solid ${T.border}`,
                    background: T.surface,
                    color: T.muted,
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: "pointer",
                    fontFamily: F,
                    transition: "background .15s",
                  }}
                >
                  <FiArrowLeft size={15} /> Cancel
                </button>

                <button
                  type="submit"
                  className="ul-btn-save"
                  disabled={saving || saved}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "11px 28px",
                    borderRadius: 10,
                    border: "none",
                    background: saved ? T.success : T.accent,
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: saving ? "not-allowed" : "pointer",
                    fontFamily: F,
                    transition: "background .2s",
                    opacity: saving ? 0.85 : 1,
                    minWidth: 140,
                    justifyContent: "center",
                  }}
                >
                  {saving ? (
                    <>
                      <FiLoader
                        size={15}
                        style={{ animation: "spin .7s linear infinite" }}
                      />{" "}
                      Saving…
                    </>
                  ) : saved ? (
                    <>
                      <FiCheckCircle size={15} /> Saved!
                    </>
                  ) : (
                    <>
                      <FiSave size={15} /> Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateLead;
